import React, { useContext, useEffect, useState } from 'react';

import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";

import ShopItemForm from "@component/ShopItemForm";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DataGrid } from "@mui/x-data-grid";
import Switch from '@mui/material/Switch';
import { useTranslation } from "react-i18next";

import { useSiteShopItemsQuery } from "@query/shopQuery";
import { useUpdateSiteShopItemAvailabilityMutation, useAttachShopItemToSiteMutation } from "@mutation/shopMutation";

const ShopItemSidePanelPage = ({ shopItemUuid }) => {
    const { club, sites } = useContext(AccountContext);
    const { addAlertBanner } = useContext(AlertContext);
    const [siteShopItems, setSiteShopItems] = useState([]);
    const siteShopItemQuery = useSiteShopItemsQuery(club.uuid, shopItemUuid);
    const { t } = useTranslation(["main", "shop"]);
    const updateSiteShopItemAvailabilityMutation = useUpdateSiteShopItemAvailabilityMutation()
    const attachShopItemToSiteMutation = useAttachShopItemToSiteMutation();

    useEffect(() => {
        if (siteShopItemQuery.data?.data) {
            var _siteShopItems = transformSiteShopItems(siteShopItemQuery.data.data)
            if (sites && sites.length > 0) {
                sites.forEach((site) => {
                    if (!_siteShopItems.some(ssi => ssi.site_uuid == site.uuid)) {
                        _siteShopItems.push(
                            {
                                name: site.name,
                                site_uuid: site.uuid
                            }
                        )
                    }
                });
            }
            setSiteShopItems(_siteShopItems);

        }
    }, [siteShopItemQuery.data]);

    const handleToggleAvailability = (siteShopItem) => {
        const siteUuid = siteShopItem.site_uuid;
        const siteShopItemUuid = siteShopItem.site_shop_item_uuid;
        const newValue = !siteShopItem.is_available;

        // Optimisticky aktualizuj UI
        setSiteShopItems((prev) =>
            prev.map((item) =>
                item.site_shop_item_uuid === siteShopItemUuid
                    ? { ...item, is_available: newValue }
                    : item
            )
        );

        updateSiteShopItemAvailabilityMutation.mutate({
            data: { siteUuid: siteUuid, siteShopItemUuid: siteShopItemUuid, is_available: newValue }
        },
            {
                onSuccess: () => {
                    addAlertBanner(t("main:saveSuccess"), "success");
                },
                onError: () => {
                    addAlertBanner(t("main:saveError"), "error");
                    setSiteShopItems((prev) =>
                        prev.map((item) =>
                            item.site_shop_item_uuid === siteShopItemUuid
                                ? { ...item, is_available: siteShopItem.is_available }
                                : item
                        )
                    );
                }
            }
        );

    };

    function attachShopItemToSite(siteShopItem) {
        attachShopItemToSiteMutation.mutate(
            {
                data:{
                    siteUuid: siteShopItem.site_uuid,
                    shopItemUuid: shopItemUuid
                }
            },
            {
                onSuccess: () => {
                    addAlertBanner(t("main:saveSuccess"), "success");
                    siteShopItemQuery.refetch();
                },
                onError: () => {
                    addAlertBanner(t("main:saveError"), "error");
                }
            }
        );
    }

    function transformSiteShopItems(items) {
        return items.map(item => ({
            name: item.site?.name || "",
            site_uuid: item.site.uuid || "",
            is_available: item.is_available ?? false,
            site_shop_item_uuid: item.uuid
        }));
    }

    const columns = [
        {
            field: 'name',
            headerName: t('shop:site'),
            flex: 1
        },
        {
            field: 'available',
            headerName: t('shop:available'),
            flex: 1,
            valueGetter: (params, row) => {
                if (Object.prototype.hasOwnProperty.call(row, 'is_available')) {
                    return row.is_available
                }
                return false
            },
            renderCell: (params) => (
                <Switch
                    checked={params.row.is_available}
                    onChange={
                        Object.prototype.hasOwnProperty.call(params.row, 'is_available')
                            ? () => handleToggleAvailability(params.row)
                            : () => attachShopItemToSite(params.row)
                    }
                />
            ),
        },
    ];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2.5 }}>

            <ShopItemForm uuid={shopItemUuid} />
            <Box sx={{ px: 2, display: "flex", gap: 2.5, flexDirection: "column" }}>
                <Typography variant="h6">{t('shop:availabilityBySite')}</Typography>

                <DataGrid
                    autoHeight
                    rows={siteShopItems}
                    getRowId={(row) => row.site_uuid}
                    columns={columns}
                    loading={siteShopItemQuery.isFetching}
                    disableRowSelectionOnClick
                    hideFooter
                />
            </Box>
        </Box>
    );
};

export default ShopItemSidePanelPage;
